import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import ExperienceCard from "@/components/ExperienceCard";
import { Container, FadeIn, SectionHeading, SectionLabel } from "@/components/Section";
import { EXPERIENCES, IMG } from "@/lib/data";
import SEOHead from "@/components/SEOHead";
import NextStepsBlock from "@/components/NextStepsBlock";
import SocialEcosystem from "@/components/SocialEcosystem";
import AcuityScheduleBlock from "@/components/AcuityScheduleBlock";
import usePageOverrides from "@/lib/usePageOverrides";

export default function Experiences() {
  const { seo, hero } = usePageOverrides("experiences", {
    seo: {
      title: "Immersive Experiences · Afro Anime Social · Immersive Brunch · TOK",
      description:
        "Ticketed immersive nights and social rituals — Afro Anime Social, Immersive Afro Brunch, film nights, wellness, exhibitions and the TOK Winter Experience. Medway · London · Kent.",
      image: IMG.immersiveNights,
    },
    hero: {
      eyebrow: "Experiences",
      title: "Worlds you step into.",
      copy: "Ticketed public experiences from Temple of Kulture — cinematic, immersive and built around culture.",
      image: IMG.redDancer,
    },
  });

  return (
    <div data-testid="experiences-page">
      <SEOHead cmsPage="experiences" title={seo.title} description={seo.description} image={seo.image} />
      <PageHero
        cmsPage="experiences"
        eyebrow={hero.eyebrow}
        title={hero.title}
        subtitle={hero.copy}
        image={hero.image}
        testId="experiences-hero"
      />

      <section className="py-24 sm:py-32">
        <Container>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {EXPERIENCES.map((e, i) => (
              <FadeIn key={e.title} delay={i * 0.05}>
                <ExperienceCard
                  testId={`experience-card-${i}`}
                  title={e.title}
                  tagline={e.tagline}
                  desc={e.desc}
                  image={e.image}
                  href={e.href}
                />
              </FadeIn>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-24 border-t border-amber-500/10 bg-black">
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <FadeIn>
              <SectionLabel>Book your night</SectionLabel>
              <SectionHeading className="mt-6">
                Tickets, recurring <br />
                <span className="text-amber-400">memberships</span> and more.
              </SectionHeading>
              <p className="mt-6 text-zinc-300 leading-relaxed text-lg max-w-md">
                Single tickets, season passes and TOK Membership (early access,
                discounts and members-only experiences) — see our calendar to
                book.
              </p>
            </FadeIn>
            <FadeIn delay={0.15}>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/whats-on"
                  data-testid="experiences-whats-on-cta"
                  className="inline-flex items-center gap-3 bg-amber-400 hover:bg-amber-300 text-black px-8 py-5 font-bold uppercase tracking-[0.25em] text-xs transition-colors"
                >
                  See full calendar <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/corporate"
                  data-testid="experiences-corporate-cta"
                  className="inline-flex items-center gap-3 border border-amber-400/40 hover:border-amber-400 text-amber-400 px-8 py-5 font-bold uppercase tracking-[0.25em] text-xs transition-colors"
                >
                  Book for your team
                </Link>
              </div>
            </FadeIn>
          </div>
        </Container>
      </section>

      <section className="py-24 border-t border-amber-500/10 bg-zinc-950">
        <Container>
          <FadeIn className="mb-10 max-w-3xl">
            <SectionLabel>Scheduled programmes</SectionLabel>
            <SectionHeading className="mt-5">
              Different from a ticket. <br />
              <span className="text-amber-400">Booked one-to-one.</span>
            </SectionHeading>
            <p className="mt-5 text-zinc-300 leading-relaxed">
              Ticketed nights (anime socials, brunches, festival stages) are
              first-come, first-served. <strong>Scheduled programmes</strong>{" "}
              — taster sessions, interviews, consultations — open up specific
              slots through our Acuity scheduling system. Reserve your slot
              below, or join the waitlist before bookings go live.
            </p>
          </FadeIn>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <AcuityScheduleBlock
              eyebrow="Under 16"
              title="Interactive Playground"
              copy="Free-to-low-cost projection, sensory and AR play sessions for our youngest visitors and their families."
              slug="interactive-playground"
              audience="Under 16"
              duration="60 mins"
              fallbackTo="/home-education"
              fallbackLabel="Join waitlist"
              testId="acuity-interactive-playground"
            />
            <AcuityScheduleBlock
              eyebrow="16 – 25"
              title="LEVEL UP — Discovery call"
              copy="Talk to our cohort lead before applying. Pathway, portfolio, funding routes."
              slug="level-up-interviews"
              audience="16 – 25"
              duration="20 mins"
              fallbackTo="/level-up-xr"
              fallbackLabel="Apply now"
              testId="acuity-level-up"
            />
            <AcuityScheduleBlock
              eyebrow="Creators"
              title="Kulture Lab studio residency"
              copy="Studio time at HYPHA: projection, capture, sound and AI tools — mentor-led."
              slug="kulture-lab"
              audience="Creators"
              duration="Half-day"
              fallbackTo="/join"
              fallbackLabel="Register interest"
              testId="acuity-kulture-lab"
            />
          </div>
        </Container>
      </section>

      <SocialEcosystem
        eyebrow="From the floor"
        title="See it before you book"
        copy="Anime socials, brunches, projection nights and Ubuntu retreats — short clips across TikTok, Instagram, YouTube and Twitch."
      />

      <NextStepsBlock
        title="Go deeper"
        steps={[
          { label: "Under 12", title: "Interactive Playground", desc: "Sensory-aware projection play for our youngest.", to: "/interactive-playground", testid: "exp-next-playground" },
          { label: "Wellness", title: "Ubuntu Immersive Retreat", desc: "Monthly mindfulness ceremony.", to: "/ubuntu-retreat", testid: "exp-next-ubuntu" },
          { label: "Programme", title: "LEVEL UP (16–25)", desc: "Where young creatives become world-builders.", to: "/level-up-xr", testid: "exp-next-level-up" },
          { label: "Creators", title: "Kulture Lab residency", desc: "Studio time, projection rig, AI tooling.", to: "/kulture-lab", testid: "exp-next-lab" },
        ]}
      />
    </div>
  );
}
